import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { format, isSameDay, isSaturday } from 'date-fns'
import { signInWithCustomToken } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'

import { useReservations } from '@/components/ReservationContext'
import { ClientProfile } from '@/hooks/use-client-profile'
import { useAuth } from '@/shared/auth'
import { auth, functions } from '@/shared/firebase/firebase'
import { useGlobalPublicConfig } from '@/shared/hooks/use-global-config'
import { generatePetId } from '@/shared/lib/pets/generate-pet-id'
import { zodResolver } from '@hookform/resolvers/zod'
import { HotelReservation } from '@monorepo/functions/src/types/reservations'
import { AdditionalService, MedicationService } from '@monorepo/functions/src/types/services'

import { BookingFormData, BookingState, PetData, PetFormData, bookingFormSchema } from '../types/booking.types'

interface UseBookingFormProps {
    defaultValues?: Partial<BookingFormData>
    clientProfile?: ClientProfile | null
    userIdFromUrl?: string | null
}

// Función para parsear dirección concatenada
function parseAddress(address: string | undefined): {
    street: string
    streetNumber: string
    city: string
    postalCode: string
    province: string
    country: string
} {
    if (!address || !address.trim()) {
        return { street: '', streetNumber: '', city: '', postalCode: '', province: '', country: '' }
    }

    const parts = address
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)

    if (parts.length >= 6) {
        return {
            street: parts[0] || '',
            streetNumber: parts[1] || '',
            city: parts[2] || '',
            postalCode: parts[3] || '',
            province: parts[4] || '',
            country: parts[5] || '',
        }
    }

    return { street: '', streetNumber: '', city: '', postalCode: '', province: '', country: '' }
}

// Función para parsear personalidad concatenada
function parsePersonality(personality: string | undefined): {
    withOtherDogs: string
    withStrangers: string
    fears: string
} {
    if (!personality || !personality.trim()) {
        return { withOtherDogs: '', withStrangers: '', fears: '' }
    }

    // Separar por doble salto de línea
    const parts = personality
        .split('\n\n')
        .map(part => part.trim())
        .filter(Boolean)

    if (parts.length >= 3) {
        return {
            withOtherDogs: parts[0] || '',
            withStrangers: parts[1] || '',
            fears: parts[2] || '',
        }
    }

    return { withOtherDogs: '', withStrangers: '', fears: '' }
}

export function useBookingForm({ defaultValues, clientProfile, userIdFromUrl }: UseBookingFormProps = {}) {
    const [searchParams] = useSearchParams()
    const { t, i18n } = useTranslation()
    const { user } = useAuth()
    const isDriverTimeManagedExternally = !user?.isStaff
    const type = searchParams.get('type')
    const { data: globalConfig } = useGlobalPublicConfig()
    const [state, setState] = useState<BookingState>({
        currentStep: 1,
        selectedDates: null,
        totalPrice: 0,
        dateError: '',
        formError: '',
        groomingUnavailable: false,
        checkInTime: '',
        checkOutTime: '',
        confirmedReservationId: '',
    })

    const { addReservation } = useReservations()
    const isSubmittingRef = useRef(false)

    // Initialize form with default empty values
    const form = useForm<BookingFormData>({
        resolver: zodResolver(bookingFormSchema),
        defaultValues: {
            pets: [
                {
                    id: generatePetId(),
                    name: '',
                    breed: '',
                    weight: 0,
                    size: 'pequeño' as const,
                    age: '',
                    personality: '',
                    personalityWithOtherDogs: '',
                    personalityWithStrangers: '',
                    personalityFears: '',
                    sex: 'M' as const,
                    isNeutered: false,
                },
            ],
            dates: null,
            services: [],
            clientName: '',
            clientLastName: '',
            clientEmail: '',
            clientPhone: '',
            clientAddress: '',
            clientStreet: '',
            clientStreetNumber: '',
            clientCity: '',
            clientPostalCode: '',
            clientProvince: '',
            clientCountry: '',
            ...defaultValues,
        },
    })

    // Handle client profile changes - this should take priority over localStorage
    useEffect(() => {
        if (clientProfile) {
            console.log('clientProfile', clientProfile)

            // Parsear dirección si existe
            const parsedAddress = parseAddress(clientProfile.address)

            // First, create the pets array preserving existing ids or generating new ones
            const petsWithIds = (clientProfile.pets && Array.isArray(clientProfile.pets) && clientProfile.pets.length > 0
                ? clientProfile.pets.map(pet => {
                    // Parsear personalidad si existe
                    const parsedPersonality = parsePersonality(pet.personality)

                    return {
                        // Preserve pet.id if it exists, otherwise generate a new one
                        id: pet.id || generatePetId(),
                        name: pet.name,
                        breed: pet.breed,
                        weight: pet.weight,
                        size: pet.size,
                        age: pet.age || '',
                        personality:
                            parsedPersonality.withOtherDogs &&
                                parsedPersonality.withStrangers &&
                                parsedPersonality.fears
                                ? ''
                                : pet.personality || '',
                        personalityWithOtherDogs: parsedPersonality.withOtherDogs,
                        personalityWithStrangers: parsedPersonality.withStrangers,
                        personalityFears: parsedPersonality.fears,
                        sex: pet.sex || 'M',
                        isNeutered: pet.isNeutered || false,
                    }
                })
                : [
                    {
                        id: generatePetId(),
                        name: '',
                        breed: '',
                        weight: 0,
                        size: 'pequeño' as const,
                        age: '',
                        personality: '',
                        personalityWithOtherDogs: '',
                        personalityWithStrangers: '',
                        personalityFears: '',
                        sex: 'M' as const,
                        isNeutered: false,
                    },
                ])

            // Now map services using the pets array we just created
            const servicesWithCorrectIndices = clientProfile.pets.flatMap((pet, originalIndex) => {
                if (!pet.additionalServices) return []

                // Find the pet in the new pets array by id or name/breed
                const petId = pet.id
                let petIndex = originalIndex

                if (petId) {
                    // Try to find by id first
                    const foundIndex = petsWithIds.findIndex(p => p.id === petId)
                    if (foundIndex !== -1) {
                        petIndex = foundIndex
                    } else {
                        // If id doesn't match, try to match by name and breed
                        const foundIndexByName = petsWithIds.findIndex(p => p.name === pet.name && p.breed === pet.breed)
                        if (foundIndexByName !== -1) {
                            petIndex = foundIndexByName
                        }
                    }
                } else {
                    // Fallback: try to match by name and breed
                    const foundIndex = petsWithIds.findIndex(p => p.name === pet.name && p.breed === pet.breed)
                    if (foundIndex !== -1) {
                        petIndex = foundIndex
                    }
                }

                // Map each service to include the correct petIndex based on pet id
                return pet.additionalServices.map(service => {
                    // Create a new service object with the correct petIndex
                    const newService = { ...service, petIndex } as AdditionalService

                    // Handle specific service types if needed
                    if (service.type === 'medication' && !(service as MedicationService).frequency) {
                        const medicationService = newService as MedicationService
                        medicationService.frequency = 'single'
                    }

                    return newService
                })
            })

            const clientProfileDefaults = {
                pets: petsWithIds,
                services: servicesWithCorrectIndices,
                clientName: clientProfile.name || '',
                clientLastName: clientProfile.lastName || '',
                clientEmail: clientProfile.email || '',
                clientPhone: clientProfile.phone || '',
                clientAddress:
                    parsedAddress.street &&
                        parsedAddress.streetNumber &&
                        parsedAddress.city &&
                        parsedAddress.postalCode &&
                        parsedAddress.province &&
                        parsedAddress.country
                        ? ''
                        : clientProfile.address || '',
                clientStreet: parsedAddress.street,
                clientStreetNumber: parsedAddress.streetNumber,
                clientCity: parsedAddress.city,
                clientPostalCode: parsedAddress.postalCode,
                clientProvince: parsedAddress.province,
                clientCountry: parsedAddress.country,
            }

            // Reset form with client profile data. Do not load dates to avoid blocked-day issues.
            setState(prev => ({
                ...prev,
                selectedDates: null,
                checkInTime: '',
                checkOutTime: '',
            }))
            form.reset({
                ...form.getValues(),
                ...clientProfileDefaults,
                dates: null,
            })
        }
    }, [clientProfile, form])

    // Establecer email del usuario si está logueado con email/contraseña y es un cliente nuevo
    useEffect(() => {
        const isEmailPasswordUser = user?.email && user?.providerData?.[0]?.providerId === 'password'
        const isNewClient = !clientProfile
        const currentEmail = form.getValues('clientEmail')

        if (isEmailPasswordUser && isNewClient && (!currentEmail || currentEmail.trim() === '')) {
            form.setValue('clientEmail', user.email!)
        }
    }, [user, clientProfile, form])


    const addPet = useCallback(() => {
        const currentPets = form.getValues('pets')
        const newPets = [
            ...currentPets,
            {
                id: generatePetId(),
                name: '',
                breed: '',
                weight: 0,
                size: 'pequeño' as const,
                age: '0',
                personality: '',
                personalityWithOtherDogs: '',
                personalityWithStrangers: '',
                personalityFears: '',
                sex: 'M' as const,
                isNeutered: false,
            },
        ]
        form.setValue('pets', newPets)
    }, [form])

    const removePet = useCallback(
        (index: number) => {
            const currentPets = form.getValues('pets')
            if (currentPets.length > 1) {
                const newPets = currentPets.filter((_, i) => i !== index)
                form.setValue('pets', newPets)

                // Update services: remove services for the removed pet and update indices for remaining pets
                const currentServices = form.getValues('services')
                const updatedServices = currentServices
                    .filter(service => {
                        // Remove services for the removed pet
                        if ('petIndex' in service && service.petIndex === index) {
                            return false
                        }
                        return true
                    })
                    .map(service => {
                        // Update petIndex for services that were after the removed pet
                        if ('petIndex' in service && service.petIndex > index) {
                            return { ...service, petIndex: service.petIndex - 1 } as AdditionalService
                        }
                        return service
                    })
                form.setValue('services', updatedServices)
            }
        },
        [form],
    )

    const validateAllSteps = useCallback(async () => {
        const isValid = await form.trigger()

        if (!isValid) {
            const errors = form.formState.errors

            if (errors.pets) {
                setState(prev => ({
                    ...prev,
                    formError: t(
                        'booking.errors.petFields',
                        'Por favor, completa todos los campos de las mascotas antes de continuar.',
                    ),
                }))
                return false
            }

            if (!state.selectedDates) {
                setState(prev => ({
                    ...prev,
                    formError: t(
                        'booking.errors.dateSelection',
                        'Por favor, selecciona las fechas de entrada y salida.',
                    ),
                }))
                return false
            }

            if (errors.clientName || errors.clientLastName || errors.clientEmail || errors.clientPhone) {
                setState(prev => ({
                    ...prev,
                    formError: t(
                        'booking.errors.contactInfo',
                        'Por favor, revisa tus datos de contacto. Hay campos inválidos o incompletos.',
                    ),
                }))
                return false
            }
        }

        setState(prev => ({ ...prev, formError: '' }))
        return isValid
    }, [form, state.selectedDates, t])

    const handleSubmit = useCallback(async () => {
        // Prevent concurrent submissions
        if (isSubmittingRef.current) {
            console.log('handleSubmit: Already submitting, ignoring duplicate call')
            return undefined
        }

        isSubmittingRef.current = true
        console.log('handleSubmit', state.currentStep)

        try {
            const isValid = await form.trigger()

            // Check if driver service is active
            const services = form.getValues('services')
            const hasDriverService = services.some(service => service.type === 'driver')
            const requiresManualDriverTimeSelection = hasDriverService && !isDriverTimeManagedExternally

            // Validate times are selected only if driver service is active
            if (requiresManualDriverTimeSelection && (!state.checkInTime || !state.checkOutTime)) {
                setState(prev => ({
                    ...prev,
                    formError: t(
                        'booking.errors.timeSelection',
                        'Por favor, selecciona la hora de recogida y entrega.',
                    ),
                }))
                return undefined
            }

            if (isValid && state.selectedDates) {
                const values = form.getValues()
                const transformedPets: PetData[] = (values.pets as PetFormData[]).map(pet => {
                    // Concatenar campos de personalidad si no hay personalidad guardada
                    let finalPersonality = pet.personality
                    if (!finalPersonality && pet.personalityWithOtherDogs) {
                        const personalityParts = [
                            pet.personalityWithOtherDogs,
                            pet.personalityWithStrangers,
                            pet.personalityFears,
                        ].filter(Boolean)
                        finalPersonality = personalityParts.join('\n\n')
                    }

                    return {
                        name: pet.name,
                        breed: pet.breed,
                        weight: Number(pet.weight),
                        size: pet.size,
                        sex: pet.sex,
                        isNeutered: pet.isNeutered || false,
                        personality: finalPersonality ?? '',
                        age: pet.age,
                    }
                })

                // Filter out any undefined or null values from services
                const cleanedServices: AdditionalService[] = values.services
                    .filter(service => service !== null)
                    .map(service => {
                        switch (service.type) {
                            case 'driver':
                                return {
                                    type: 'driver' as const,
                                    petIndex: service.petIndex,
                                    serviceType: service.serviceType,
                                }
                            case 'special_food':
                                return {
                                    type: 'special_food' as const,
                                    petIndex: service.petIndex,
                                    foodType: service.foodType,
                                }
                            case 'medication':
                                return {
                                    type: 'medication' as const,
                                    petIndex: service.petIndex,
                                    comment: service.comment,
                                    frequency: service.frequency,
                                }
                            case 'special_care':
                                return {
                                    type: 'special_care' as const,
                                    petIndex: service.petIndex,
                                    comment: service.comment,
                                }
                            case 'hairdressing':
                                return {
                                    type: 'hairdressing' as const,
                                    petIndex: service.petIndex,
                                    services: service.services,
                                }
                            default:
                                return service as AdditionalService
                        }
                    })

                // Concatenar campos de dirección si no hay dirección guardada
                let finalAddress = values.clientAddress || ''
                if (!finalAddress && values.clientStreet) {
                    const addressParts = [
                        values.clientStreet,
                        values.clientStreetNumber,
                        values.clientCity,
                        values.clientPostalCode,
                        values.clientProvince,
                        values.clientCountry,
                    ].filter(Boolean)
                    finalAddress = addressParts.join(', ')
                }

                const isEmailPasswordUser = user?.email && user?.providerData?.[0]?.providerId === 'password'
                const isNewClient = !clientProfile
                const clientEmail = isEmailPasswordUser && isNewClient ? user.email! : values.clientEmail

                const newReservation: Omit<HotelReservation, 'id' | 'clientId'> = {
                    type: 'hotel' as const,
                    status: type === 'budget' ? ('budget' as const) : ('pending' as const),
                    checkInDate: format(state.selectedDates.from, 'yyyy-MM-dd'),
                    checkOutDate: format(state.selectedDates.to, 'yyyy-MM-dd'),
                    checkInTime: state.checkInTime,
                    checkOutTime: state.checkOutTime
                        ? (state.checkOutTime as HotelReservation['checkOutTime'])
                        : undefined,
                    client: {
                        name: values.clientName,
                        lastName: values.clientLastName,
                        phone: values.clientPhone,
                        email: clientEmail,
                        pets: transformedPets,
                        address: finalAddress,
                        classification: 'normal',
                        language: (i18n.language === 'en' ? 'en' : 'es') as 'en' | 'es',
                    },
                    pets: transformedPets,
                    additionalServices: cleanedServices,
                    totalPrice: state.totalPrice,
                    paymentStatus: 'pending',
                    hairSalonReservationStatus: 'not-requested',
                }

                try {
                    // If user provided password for existing client, link the account first
                    // Skip this flow if user is staff or if there's a userId in the URL
                    const newPassword = (values as any).newPassword
                    const confirmPassword = (values as any).confirmPassword

                    if (newPassword && confirmPassword && !user?.isStaff && !userIdFromUrl) {
                        // Validate passwords match
                        if (newPassword !== confirmPassword) {
                            setState(prev => ({
                                ...prev,
                                formError: t('auth.errors.passwordMismatch', 'Las contraseñas no coinciden'),
                            }))
                            return undefined
                        }

                        // Validate password length
                        if (newPassword.length < 8) {
                            setState(prev => ({
                                ...prev,
                                formError: t(
                                    'auth.errors.weakPassword',
                                    'La contraseña debe tener al menos 8 caracteres',
                                ),
                            }))
                            return undefined
                        }

                        try {
                            const linkExistingClient = httpsCallable(functions, 'linkExistingClient')
                            const result = await linkExistingClient({
                                email: values.clientEmail,
                                password: newPassword,
                            })

                            const { token } = result.data as { token: string }
                            // Sign in with the new account
                            await signInWithCustomToken(auth, token)
                        } catch (error: any) {
                            console.error('Error linking client account:', error)
                            let errorMessage = t('auth.errors.unknown', 'Ocurrió un error. Por favor intenta de nuevo.')

                            if (error.code === 'functions/already-exists') {
                                errorMessage = t('auth.errors.emailInUse', 'Ya existe una cuenta con ese email')
                            }

                            setState(prev => ({
                                ...prev,
                                formError: errorMessage,
                            }))
                            return undefined
                        }
                    }

                    console.log('newReservation', newReservation)
                    const savedReservation = await addReservation(newReservation)
                    setState(prev => ({
                        ...prev,
                        confirmedReservationId: savedReservation.id,
                        checkInTime: '',
                        checkOutTime: '',
                    }))
                    localStorage.setItem('bookedReservation', JSON.stringify(savedReservation))
                    return savedReservation.id
                } catch (error) {
                    console.error('Error saving reservation:', error)
                    setState(prev => ({
                        ...prev,
                        formError: t(
                            'booking.errors.saveReservation',
                            'Hubo un error al guardar la reserva. Por favor, inténtalo de nuevo.',
                        ),
                    }))
                    return undefined
                }
            } else {
                setState(prev => ({
                    ...prev,
                    formError: t(
                        'booking.errors.incompleteForm',
                        'Por favor, revisa todos los campos. Hay información incompleta o inválida.',
                    ),
                }))
                return undefined
            }
        } finally {
            isSubmittingRef.current = false
        }
    }, [
        form,
        state.selectedDates,
        state.checkInTime,
        state.checkOutTime,
        state.totalPrice,
        state.currentStep,
        addReservation,
        t,
        type,
        i18n.language,
        clientProfile,
        user,
        userIdFromUrl,
        isDriverTimeManagedExternally,
    ])

    const nextStep = useCallback(async () => {
        let isValid = false

        setState(prev => ({ ...prev, formError: '' }))
        console.log('state.currentStep', state.currentStep)
        switch (state.currentStep) {
            case 1: {
                isValid = await form.trigger('pets', { shouldFocus: true })
                console.log('isValid', isValid)

                // Check if there are 3 pets with different sizes
                const pets = form.getValues('pets')
                if (pets.length === 3 && !pets.every(pet => pet.size === pets[0].size)) {
                    setState(prev => ({
                        ...prev,
                        formError: t(
                            'booking.errors.differentSizePets',
                            'No se pueden reservar 3 perros de diferentes tamaños a través de esta página. Por favor, llámanos al {{phoneNumber}} para gestionar tu reserva.',
                            {
                                phoneNumber: globalConfig?.phoneNumber || '',
                            },
                        ),
                    }))
                    return
                }

                if (!isValid) {
                    setState(prev => ({
                        ...prev,
                        formError: t(
                            'booking.errors.petFields',
                            'Por favor, completa todos los campos de las mascotas antes de continuar.',
                        ),
                    }))
                }
                break
            }
            case 2: {
                if (!state.selectedDates) {
                    setState(prev => ({
                        ...prev,
                        dateError: t(
                            'booking.errors.dateSelection',
                            'Por favor, selecciona las fechas de entrada y salida.',
                        ),
                    }))
                    return
                }

                // Check for Saturday-only daycare restriction
                if (state.selectedDates.from && state.selectedDates.to) {
                    const isDaycare = isSameDay(state.selectedDates.from, state.selectedDates.to)
                    const isSaturdayDaycare = isDaycare && isSaturday(state.selectedDates.from)

                    if (isSaturdayDaycare) {
                        setState(prev => ({
                            ...prev,
                            dateError: t(
                                'booking.errors.saturdayDaycareNotAllowed',
                                'No se pueden hacer reservas de solo guardería los sábados.',
                            ),
                        }))
                        return
                    }
                }

                const services = form.getValues('services')
                const hasDriverService = services.some(service => service.type === 'driver')
                const requiresManualDriverTimeSelection = hasDriverService && !isDriverTimeManagedExternally

                // Validate that check-in and check-out times are always selected
                if (requiresManualDriverTimeSelection && (!state.checkInTime || !state.checkOutTime)) {
                    setState(prev => ({
                        ...prev,
                        dateError: t(
                            'booking.errors.timeSelectionRequired',
                            'Por favor, selecciona la hora de entrada y salida.',
                        ),
                    }))
                    // Scroll to time selection section after a short delay to allow DOM update
                    setTimeout(() => {
                        const timeSection = document.getElementById('time-selection-section')
                        if (timeSection) {
                            timeSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        }
                    }, 100)
                    return
                }

                isValid = true
                break
            }
            case 3: {
                const services = form.getValues('services')
                const specialCareServices = services.filter(service => service.type === 'special_care')
                const invalidSpecialCare = specialCareServices.some(service => !service.comment?.trim())

                if (invalidSpecialCare) {
                    setState(prev => ({
                        ...prev,
                        formError: t(
                            'booking.errors.specialCare',
                            'Por favor, escribe las instrucciones para los cuidados especiales antes de continuar.',
                        ),
                    }))
                    return
                }
                isValid = true
                break
            }
            case 4:
                isValid = await form.trigger()
                break
        }

        if (isValid) {
            setState(prev => ({ ...prev, currentStep: Math.min(prev.currentStep + 1, 4) }))
            window.scrollTo({ top: 0 })
        }
    }, [
        form,
        state.currentStep,
        state.selectedDates,
        state.checkInTime,
        state.checkOutTime,
        t,
        globalConfig?.phoneNumber,
        isDriverTimeManagedExternally,
    ])

    const prevStep = useCallback(() => {
        setState(prev => ({
            ...prev,
            formError: '',
            dateError: '',
            currentStep: Math.max(prev.currentStep - 1, 1),
        }))
        window.scrollTo({ top: 0 })
    }, [])

    const clearForm = useCallback(() => {
        form.reset({
            pets: [
                {
                    id: generatePetId(),
                    name: '',
                    breed: '',
                    weight: 0,
                    size: 'pequeño' as const,
                    age: '',
                    personality: '',
                    personalityWithOtherDogs: '',
                    personalityWithStrangers: '',
                    personalityFears: '',
                    sex: 'M' as const,
                    isNeutered: false,
                },
            ],
            dates: null,
            services: [],
            clientName: '',
            clientLastName: '',
            clientEmail: '',
            clientPhone: '',
            clientAddress: '',
            clientStreet: '',
            clientStreetNumber: '',
            clientCity: '',
            clientPostalCode: '',
            clientProvince: '',
            clientCountry: '',
        })
        setState({
            currentStep: 1,
            selectedDates: null,
            totalPrice: 0,
            dateError: '',
            formError: '',
            groomingUnavailable: false,
            checkInTime: '',
            checkOutTime: '',
            confirmedReservationId: '',
        })
    }, [form])

    return {
        form,
        state,
        setState,
        addPet,
        removePet,
        validateAllSteps,
        handleSubmit,
        nextStep,
        prevStep,
        clearForm,
    }
}
